import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  useToast
} from "@chakra-ui/react";
import { PageCardComponent } from "modules/core/components/page-card.component";
import { Select } from "chakra-react-select";
import { useCreateUserMutation, useGetAllDepartmentsQuery } from "../api";

interface Props {}

const designationOptions = [
  { value: "Doctor", label: "Doctor" },
  { value: "Nurse", label: "Nurse" },
  { value: "Other", label: "Other" }
];
export const CreateUserFormComponent: React.FC<Props> = (props: Props) => {
  const [createUser, createUserResult] = useCreateUserMutation();
  const getAllDepartmentsResult = useGetAllDepartmentsQuery({});
  const toast = useToast();

  const [name, setName] = useState<string>();
  const [contactNumber, setContactNumber] = useState<string>();
  const [nationalIdNumber, setNationalIdNumber] = useState<string>();
  const [selectedDesignationName, setSelectedDesignationName] = useState<{
    label: string;
    value: string;
  }>();
  const [departmentList, setDepartmentList] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<{
    label: string;
    value: string;
  }>();
  const [medicalCertificationNumber, setMedicalCertificationNumber] =
    useState<string>();
  const [employmentId, setEmploymentId] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>();

  useEffect(() => {
    if (getAllDepartmentsResult.data) {
      setDepartmentList(
        getAllDepartmentsResult.data.locations.map((location) => {
          return {
            label: location.name,
            value: location.id
          };
        })
      );
    }
  }, [getAllDepartmentsResult]);

  const handleUserCreation = () => {
    if (
      name &&
      contactNumber &&
      nationalIdNumber &&
      selectedDesignationName &&
      employmentId &&
      ((selectedDesignationName.value === "Doctor" &&
        medicalCertificationNumber) ||
        (selectedDesignationName.value === "Nurse" &&
          medicalCertificationNumber) ||
        (selectedDesignationName.value === "Others" &&
          !medicalCertificationNumber)) &&
      email &&
      password &&
      passwordConfirmation &&
      password === passwordConfirmation
    ) {
      createUser({
        data: {
          user: {
            email,
            password,
            designationName: selectedDesignationName.value,
            employmentId,
            medicalCertificationNumber: medicalCertificationNumber
              ? medicalCertificationNumber
              : null,
            departmentName: selectedDepartmentName?.value
              ? selectedDepartmentName.value
              : null
          },
          person: {
            name: name,
            contactNumber,
            nationalIdNumber,
            passportNumber: null,
            gender: null,
            dateOfBirth: null,
            isDateOfBirthEstimated: null,
            dateOfDeath: null,
            isDateOfDeathEstimated: null,
            religionName: null,
            fatherName: null,
            spouseName: null
          },
          address: {
            addressLine1: null,
            addressLine2: null,
            postalCode: null,
            unionCode: null,
            subdistrictCode: null,
            districtCode: null,
            divisionCode: null
          }
        }
      });
    }
  };

  const handleReset = () => {
    setName("");
    setContactNumber("");
    setNationalIdNumber("");
    setSelectedDesignationName({ label: "", value: "" });
    setSelectedDepartmentName({ label: "", value: "" });
    setMedicalCertificationNumber("");
    setEmploymentId("");
    setEmail("");
    setPassword("");
    setPasswordConfirmation("");
  };

  useEffect(() => {
    if (createUserResult.isSuccess) {
      toast({
        title: "Success",
        description: "User created successfully",
        status: "success",
        position: "top",
        duration: 2000,
        isClosable: true
      });
      handleReset();
    }
  }, [createUserResult]);

  return (
    <Stack>
      <PageCardComponent>
        <Heading fontSize={{ base: "sm", md: "sm", lg: "md", xl: "lg" }}>
          Personal Information
        </Heading>
        <Stack direction={{ base: "column", md: "row", lg: "row", xl: "row" }}>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>Name</FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              Contact Number
            </FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              value={contactNumber}
              onChange={(event) => {
                setContactNumber(event.target.value);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              National ID Number
            </FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              value={nationalIdNumber}
              onChange={(event) => {
                setNationalIdNumber(event.target.value);
              }}
            />
          </FormControl>
        </Stack>

        <Divider />

        <Heading fontSize={{ base: "sm", md: "sm", lg: "md", xl: "lg" }}>
          Professional Information
        </Heading>
        <Stack direction={{ base: "column", md: "row", lg: "row", xl: "row" }}>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              Department Name
            </FormLabel>
            <Select
              selectedOptionStyle="check"
              options={departmentList}
              value={selectedDepartmentName}
              onChange={(event) => {
                if (event) {
                  setSelectedDepartmentName({
                    label: event.label,
                    value: event.value
                  });
                }
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              Profession
            </FormLabel>
            <Select
              size="sm"
              selectedOptionStyle="check"
              options={designationOptions}
              value={selectedDesignationName}
              onChange={(event) => {
                if (event) {
                  setSelectedDesignationName({
                    label: event.label,
                    value: event.value
                  });
                }
              }}
            />
          </FormControl>
        </Stack>
        <Stack direction={{ base: "column", md: "row", lg: "row", xl: "row" }}>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              HRIS Number
            </FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              value={employmentId}
              onChange={(event) => {
                setEmploymentId(event.target.value);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              BMDC/BNC Number (Doctor/Nurse)
            </FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              type="text"
              value={medicalCertificationNumber}
              isDisabled={selectedDesignationName?.value === "Others"}
              onChange={(event) => {
                setMedicalCertificationNumber(event.target.value);
              }}
            />
          </FormControl>
        </Stack>

        <Divider />

        <Heading fontSize={{ base: "sm", md: "sm", lg: "md", xl: "lg" }}>
          Account Information
        </Heading>
        <Stack direction={{ base: "column", md: "row", lg: "row", xl: "row" }}>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>Email</FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>Password</FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={{ base: "xs", lg: "md" }}>
              Confirm Password
            </FormLabel>
            <Input
              size={{ base: "xs", lg: "sm" }}
              type="password"
              value={passwordConfirmation}
              onChange={(event) => {
                setPasswordConfirmation(event.target.value);
              }}
            />
          </FormControl>
        </Stack>

        <Divider pb={4} />
        <Stack direction="row">
          <Button
            size={{ base: "xs", lg: "sm" }}
            onClick={handleUserCreation}
            isLoading={createUserResult.isLoading}
            isDisabled={
              !(
                name &&
                contactNumber &&
                nationalIdNumber &&
                selectedDesignationName &&
                ((selectedDesignationName.value === "Doctor" &&
                  medicalCertificationNumber) ||
                  (selectedDesignationName.value !== "Doctor" &&
                    !medicalCertificationNumber)) &&
                email &&
                password &&
                passwordConfirmation &&
                password === passwordConfirmation
              )
            }
            width="100%"
          >
            Create User
          </Button>
          <Button
            size={{ base: "xs", lg: "sm" }}
            onClick={handleReset}
            colorScheme={"red"}
            width="100%"
          >
            Reset
          </Button>
        </Stack>
      </PageCardComponent>
    </Stack>
  );
};
